import { HistoryOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Modal,
  message,
  Pagination,
  Segmented,
  Skeleton,
  Space,
  Spin,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CrmList } from '@/components/CrmList';
import Empty from '@/components/QuietEmpty';
import { DetailSurface } from '@/modules/zy-crm/components/detail-surface';
import {
  createDrawerAssistSummaryItems,
  DrawerAssistPanel,
  type DrawerAssistSummary,
} from '@/modules/zy-crm/components/drawer-assist-panel';
import {
  ensureChatModelReady,
  getCrmErrorCode,
  showAiModelConfigDialog,
} from '@/modules/zy-crm/utils/ai-model-readiness';
import { formatCrmDate } from '@/modules/zy-crm/utils/display';
import {
  queryCrmAiSummary,
  queryCrmAiSummaryHistory,
  streamCrmAiSummary,
} from '@/services/bff/ai-summary.api';
import type { CrmAiBizType } from '@/services/bff/types/ai-biz';
import type {
  AiSummaryHistoryItem,
  CrmAiSummaryPanel,
} from '@/services/bff/types/ai-summary';
import type { ContractDrawerSummary } from '@/services/bff/types/contract';
import type { CustomerDrawerSummary } from '@/services/bff/types/customer';
import type { LeadDrawerSummary } from '@/services/bff/types/lead';
import type { OpportunityDrawerSummary } from '@/services/bff/types/opportunity';
import type { QuoteDrawerSummary } from '@/services/bff/types/quote';
import { AiChatPanel } from './AiChatPanel';
import styles from './style.module.less';

type SupportedSummaryCounts =
  | LeadDrawerSummary
  | CustomerDrawerSummary
  | ContractDrawerSummary
  | OpportunityDrawerSummary
  | QuoteDrawerSummary
  | undefined;

type AiPanelTab = 'summary' | 'chat';

const buildSummaryData = (
  prefix: string,
  summary?: CrmAiSummaryPanel,
  extraBullets: string[] = [],
): DrawerAssistSummary | undefined => {
  if (!summary || summary.enabled === false) {
    return undefined;
  }

  return {
    title: summary.title,
    generatedAt: summary.generatedAt,
    overview: summary.overview,
    bullets: createDrawerAssistSummaryItems(prefix, [
      ...summary.bullets,
      ...extraBullets,
    ]),
    suggestions: createDrawerAssistSummaryItems(
      `${prefix}-suggestion`,
      summary.suggestions || [],
    ),
  };
};

const SUMMARY_CONFIG: Record<
  CrmAiBizType,
  {
    prefix: string;
    emptyText: string;
    buildExtraBullets: (summaryCounts?: SupportedSummaryCounts) => string[];
  }
> = {
  lead: {
    prefix: 'lead-summary',
    emptyText: '',
    buildExtraBullets: (summaryCounts) => {
      const counts = summaryCounts as LeadDrawerSummary | undefined;
      if (!counts) return [];
      return [
        `静默天数：${
          counts.unfollowedDays === null ? '—' : `${counts.unfollowedDays} 天`
        }`,
      ];
    },
  },
  customer: {
    prefix: 'customer-summary',
    emptyText: '',
    buildExtraBullets: (summaryCounts) => {
      const counts = summaryCounts as CustomerDrawerSummary | undefined;
      if (!counts) return [];
      return [
        `联系人 ${counts.contactCount} 位，商机 ${counts.opportunityCount} 条，合同 ${counts.contractCount} 份`,
        `待办 ${counts.pendingCount} 项，静默天数 ${
          counts.unfollowedDays === null ? '—' : `${counts.unfollowedDays} 天`
        }`,
      ];
    },
  },
  opportunity: {
    prefix: 'opportunity-summary',
    emptyText: '',
    buildExtraBullets: (summaryCounts) => {
      const counts = summaryCounts as OpportunityDrawerSummary | undefined;
      if (!counts) return [];
      return [
        `活动 ${counts.activityCount} 条，联系人 ${counts.contactCount} 位`,
        `报价 ${counts.quoteCount} 条，合同 ${counts.contractCount} 条，产品 ${counts.productCount} 条`,
      ];
    },
  },
  contact: {
    prefix: 'contact-summary',
    emptyText: '',
    buildExtraBullets: () => [],
  },
  contract: {
    prefix: 'contract-summary',
    emptyText: '',
    buildExtraBullets: (summaryCounts) => {
      const counts = summaryCounts as ContractDrawerSummary | undefined;
      if (!counts) return [];
      return [
        `产品 ${counts.productCount} 条，回款 ${counts.receiptCount} 条，发票 ${counts.invoiceCount} 条`,
        `待回款 ${counts.outstandingAmount || '0'}，静默天数 ${
          counts.daysSinceOrder === null ? '—' : `${counts.daysSinceOrder} 天`
        }`,
      ];
    },
  },
  quote: {
    prefix: 'quote-summary',
    emptyText: '',
    buildExtraBullets: (summaryCounts) => {
      const counts = summaryCounts as QuoteDrawerSummary | undefined;
      if (!counts) return [];
      return [
        `产品 ${counts.productCount} 条，合同 ${counts.contractCount} 条`,
        `活动 ${counts.activityCount} 条，协作成员 ${counts.teamMemberCount} 位`,
      ];
    },
  },
};

function SummaryHistoryModal({
  open,
  loading,
  error,
  list,
  total,
  page,
  pageSize,
  selectedUuid,
  onClose,
  onSelect,
  onPageChange,
}: {
  open: boolean;
  loading: boolean;
  error?: Error;
  list: AiSummaryHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
  selectedUuid?: string;
  onClose: () => void;
  onSelect: (uuid: string) => void;
  onPageChange: (nextPage: number, nextPageSize: number) => void;
}) {
  const selectedItem = useMemo(
    () => list.find((item) => item.uuid === selectedUuid),
    [list, selectedUuid],
  );

  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      title="历史摘要"
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      destroyOnHidden={false}
    >
      <div className={styles.historyModal}>
        <div className={styles.historySidebar}>
          <div className={styles.historySidebarHeader}>历史记录</div>
          {loading ? (
            <div className={styles.historyLoading}>
              <Spin />
            </div>
          ) : error ? (
            <Alert
              type="error"
              showIcon
              message="历史摘要加载失败"
              description={error.message}
            />
          ) : list.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
          ) : (
            <>
              <CrmList
                split={false}
                className={styles.historyList}
                dataSource={list}
                renderItem={(item) => {
                  const active = item.uuid === selectedUuid;
                  return (
                    <CrmList.Item
                      className={
                        active
                          ? styles.historyListItemActive
                          : styles.historyListItem
                      }
                      onClick={() => onSelect(item.uuid)}
                    >
                      <div className={styles.historyItemTitle}>
                        {item.title || '总结分析'}
                      </div>
                      <div className={styles.historyItemTime}>
                        {item.generatedAt
                          ? formatCrmDate(item.generatedAt)
                          : '-'}
                      </div>
                      <div className={styles.historyItemOverview}>
                        {item.overview || '—'}
                      </div>
                    </CrmList.Item>
                  );
                }}
              />
              <Pagination
                size="small"
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={onPageChange}
                className={styles.historyPagination}
              />
            </>
          )}
        </div>
        <div className={styles.historyDetail}>
          {loading ? (
            <Skeleton active paragraph={{ rows: 10 }} />
          ) : error ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="历史摘要暂不可用"
            />
          ) : !selectedItem ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="请选择历史摘要"
            />
          ) : (
            <div className={styles.historyDetailContent}>
              <Space
                orientation="vertical"
                size={6}
                className={styles.historyDetailHeader}
              >
                <Typography.Title
                  level={5}
                  className={styles.historyDetailTitle}
                >
                  {selectedItem.title || '总结分析'}
                </Typography.Title>
                <Typography.Text type="secondary">
                  生成时间{' '}
                  {selectedItem.generatedAt
                    ? formatCrmDate(selectedItem.generatedAt)
                    : '-'}
                </Typography.Text>
              </Space>
              <div className={styles.historySection}>
                <div className={styles.historySectionTitle}>概述</div>
                <Typography.Paragraph className={styles.historyParagraph}>
                  {selectedItem.overview || '-'}
                </Typography.Paragraph>
              </div>
              <div className={styles.historySection}>
                <div className={styles.historySectionTitle}>要点</div>
                <ul className={styles.historyBulletList}>
                  {selectedItem.bullets.map((item) => (
                    <li key={`${selectedItem.uuid}-bullet-${item}`}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className={styles.historySection}>
                <div className={styles.historySectionTitle}>建议</div>
                <ul className={styles.historyBulletList}>
                  {selectedItem.suggestions.map((item) => (
                    <li key={`${selectedItem.uuid}-suggestion-${item}`}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              {selectedItem.provider || selectedItem.model ? (
                <Typography.Text
                  type="secondary"
                  className={styles.historyMeta}
                >
                  模型信息 {selectedItem.provider || '-'} /{' '}
                  {selectedItem.model || '-'}
                </Typography.Text>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function AiDetailPanel({
  bizType,
  bizUuid,
  initialSummary,
  summaryCounts,
  activeTab,
  contextTitle,
}: {
  bizType: CrmAiBizType;
  bizUuid: string;
  initialSummary?: CrmAiSummaryPanel;
  summaryCounts?: SupportedSummaryCounts;
  activeTab: AiPanelTab;
  contextTitle?: string;
}) {
  const [mode, setMode] = useState<AiPanelTab>(activeTab);
  const config = SUMMARY_CONFIG[bizType];
  const [summary, setSummary] = useState<CrmAiSummaryPanel | undefined>(
    initialSummary,
  );
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryStreaming, setSummaryStreaming] = useState(false);
  const [summaryError, setSummaryError] = useState<Error>();
  const [streamText, setStreamText] = useState('');
  const [abortController, setAbortController] = useState<
    AbortController | undefined
  >(undefined);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<Error>();
  const [historyList, setHistoryList] = useState<AiSummaryHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [selectedHistoryUuid, setSelectedHistoryUuid] = useState<string>();

  useEffect(() => {
    setMode(activeTab);
  }, [activeTab]);

  useEffect(() => {
    setSummary(initialSummary);
    setSummaryError(undefined);
    setStreamText('');
  }, [bizUuid, initialSummary]);

  useEffect(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    setAbortController(undefined);
    setSummaryStreaming(false);
    setHistoryOpen(false);
    setHistoryLoading(false);
    setHistoryError(undefined);
    setHistoryList([]);
    setHistoryTotal(0);
    setHistoryPage(1);
    setHistoryPageSize(10);
    setSelectedHistoryUuid(undefined);
  }, [bizUuid]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const loadSummary = useCallback(async () => {
    if (!bizUuid) return;
    setSummaryLoading(true);
    setSummaryError(undefined);
    try {
      const ready = await ensureChatModelReady();
      if (!ready) {
        setSummary(undefined);
        return;
      }
      const data = await queryCrmAiSummary(bizType, bizUuid);
      setSummary(data || undefined);
    } catch (nextError) {
      if (getCrmErrorCode(nextError) === 'CRM_AI_MODEL_NOT_BOUND') {
        showAiModelConfigDialog();
        setSummary(undefined);
        return;
      }
      setSummaryError(
        nextError instanceof Error ? nextError : new Error('AI 总结加载失败'),
      );
      setSummary(undefined);
    } finally {
      setSummaryLoading(false);
    }
  }, [bizType, bizUuid]);

  useEffect(() => {
    if (!bizUuid) return;
    void loadSummary();
  }, [bizUuid, loadSummary]);

  const handleGenerateSummary = useCallback(async () => {
    if (!bizUuid) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setAbortController(controller);
    setSummaryLoading(false);
    setSummaryStreaming(true);
    setSummaryError(undefined);
    setStreamText('');
    try {
      const ready = await ensureChatModelReady();
      if (!ready) {
        setSummary(undefined);
        return;
      }
      await streamCrmAiSummary(bizType, bizUuid, {
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === 'start') {
            setSummary(undefined);
            setMode('summary');
            return;
          }
          if (event.type === 'chunk') {
            setStreamText((current) => current + event.text);
            return;
          }
          if (event.type === 'complete') {
            setSummary(event.summary);
            setStreamText('');
          }
        },
      });
      setMode('summary');
    } catch (nextError) {
      if (
        nextError instanceof DOMException &&
        nextError.name === 'AbortError'
      ) {
        return;
      }
      if (getCrmErrorCode(nextError) === 'CRM_AI_MODEL_NOT_BOUND') {
        showAiModelConfigDialog();
        setSummary(undefined);
        return;
      }
      const resolvedError =
        nextError instanceof Error ? nextError : new Error('AI 总结生成失败');
      setSummaryError(resolvedError);
      setSummary(undefined);
      message.error(resolvedError.message);
      setMode('summary');
    } finally {
      setAbortController(undefined);
      abortControllerRef.current = undefined;
      setSummaryStreaming(false);
      setSummaryLoading(false);
    }
  }, [bizType, bizUuid]);

  const handleStopSummaryStream = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    setAbortController(undefined);
    setSummaryStreaming(false);
  }, []);

  const loadHistory = useCallback(
    async (page = 1, pageSize = historyPageSize) => {
      if (!bizUuid) return;
      setHistoryLoading(true);
      setHistoryError(undefined);
      try {
        const data = await queryCrmAiSummaryHistory(bizType, bizUuid, {
          page,
          pageSize,
        });
        const list = data?.list || [];
        setHistoryList(list);
        setHistoryTotal(Number(data?.total || 0));
        setHistoryPage(Number(data?.page || page));
        setHistoryPageSize(Number(data?.pageSize || pageSize));
        setSelectedHistoryUuid(list[0]?.uuid);
      } catch (nextError) {
        setHistoryError(
          nextError instanceof Error
            ? nextError
            : new Error('历史摘要加载失败'),
        );
        setHistoryList([]);
        setHistoryTotal(0);
        setSelectedHistoryUuid(undefined);
      } finally {
        setHistoryLoading(false);
      }
    },
    [bizType, bizUuid, historyPageSize],
  );

  const handleOpenHistory = useCallback(async () => {
    if (!bizUuid) return;
    setHistoryOpen(true);
    await loadHistory(1, historyPageSize);
  }, [bizUuid, historyPageSize, loadHistory]);

  const hasSummary =
    summary?.enabled !== false &&
    Boolean(
      summary?.overview ||
        summary?.bullets?.length ||
        summary?.suggestions?.length,
    );
  const summaryActionLabel = hasSummary ? '重新生成摘要' : '生成摘要';
  const isGenerating = summaryStreaming || Boolean(abortController);
  const showStreamingSummary = isGenerating;
  const modeOptions = [
    { label: '摘要', value: 'summary' as const },
    { label: '对话', value: 'chat' as const },
  ];

  return (
    <>
      <DetailSurface
        title="AI 助手"
        extra={
          <Space size={12} wrap className={styles.toolbar}>
            <Segmented
              aria-label="AI 助手视图切换"
              options={modeOptions}
              value={mode}
              onChange={(value) => setMode(value as AiPanelTab)}
            />
          </Space>
        }
        className={styles.panelCard}
      >
        {mode === 'summary' ? (
          summaryLoading ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : summaryError ? (
            <Alert
              type="error"
              showIcon
              message="AI 助手加载失败"
              description={summaryError.message}
              action={
                <Button
                  size="small"
                  onClick={() => void handleGenerateSummary()}
                >
                  重试
                </Button>
              }
            />
          ) : (
            <div className={styles.summaryContent} data-crm-ai-content>
              <div className={styles.summaryActions} data-crm-ai-actions>
                <Button
                  icon={<HistoryOutlined />}
                  aria-label="历史摘要"
                  disabled={isGenerating}
                  onClick={() => void handleOpenHistory()}
                >
                  历史摘要
                </Button>
                <Button
                  type="primary"
                  aria-label={
                    isGenerating ? '停止生成摘要' : summaryActionLabel
                  }
                  onClick={() =>
                    isGenerating
                      ? handleStopSummaryStream()
                      : void handleGenerateSummary()
                  }
                >
                  {isGenerating ? '停止生成' : summaryActionLabel}
                </Button>
              </div>
              {showStreamingSummary ? (
                <div className={styles.streamingPanel}>
                  <div className={styles.streamingTitle}>正在生成摘要</div>
                  <Typography.Paragraph
                    className={styles.streamingText}
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {streamText || '正在生成摘要...'}
                  </Typography.Paragraph>
                </div>
              ) : (
                <DrawerAssistPanel
                  summary={buildSummaryData(
                    config.prefix,
                    summary,
                    config.buildExtraBullets(summaryCounts),
                  )}
                  emptyText={config.emptyText}
                  showTitle={false}
                />
              )}
            </div>
          )
        ) : (
          <AiChatPanel
            bizType={bizType}
            bizUuid={bizUuid}
            contextTitle={contextTitle}
          />
        )}
      </DetailSurface>

      <SummaryHistoryModal
        open={historyOpen}
        loading={historyLoading}
        error={historyError}
        list={historyList}
        total={historyTotal}
        page={historyPage}
        pageSize={historyPageSize}
        selectedUuid={selectedHistoryUuid}
        onClose={() => setHistoryOpen(false)}
        onSelect={setSelectedHistoryUuid}
        onPageChange={(nextPage, nextPageSize) => {
          void loadHistory(nextPage, nextPageSize);
        }}
      />
    </>
  );
}
